import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, message, Spin, Alert, Tag, Space, Select, Modal, Input, Table, Tooltip, Upload, Dropdown, Segmented
} from 'antd';
import {
  FileTextOutlined, EditOutlined, ReloadOutlined, TranslationOutlined, CheckOutlined, SaveOutlined, UndoOutlined,
  UploadOutlined, DownOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import AuthService from '../../utils/auth';
import MarkdownContent from '../../components/MarkdownContent';
import ItineraryEnCard from './ItineraryEnCard';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { TextArea } = Input;

// 出团模板（Destination）里通知书用到的段落：字段名 → 显示名
const STANDARD_SECTIONS = [
  ['gatheringInfo', '集合与出发说明'],
  ['domesticTransportInfo', '境内交通安排'],
  ['entryGuide', '入境指南'],
  ['customsInfo', '海关申报'],
  ['vaccineInfo', '疫苗说明'],
  ['localNotices', '注意事项'],
  ['insuranceInfo', '旅游保险'],
  ['paymentInfo', '支付信息'],
  ['weatherInfo', '天气信息'],
];

const DAYTOUR_SECTIONS = [
  ['gatheringInfo', '集合与出发说明'],
  ['scheduleRef', '行程时间参考'],
  ['mealArrangement', '餐食安排'],
  ['consumptionInfo', '小费说明'],
  ['costInclusion', '费用包含与不包含'],
  ['playNotices', '游玩注意事项'],
  ['importantTips', '重要提示'],
];

// 另存为新模板时需要复制的全部内容字段
const TEMPLATE_COPY_FIELDS = [
  'departureGuide', 'departureGuideEn',
  'gatheringInfo', 'gatheringInfoEn', 'domesticTransportInfo', 'domesticTransportInfoEn',
  'entryGuide', 'entryGuideEn', 'customsInfo', 'customsInfoEn',
  'vaccineInfo', 'vaccineInfoEn', 'localNotices', 'localNoticesEn',
  'insuranceInfo', 'insuranceInfoEn', 'paymentInfo', 'paymentInfoEn',
  'weatherInfo', 'weatherInfoEn',
  'scheduleRef', 'scheduleRefEn', 'mealArrangement', 'mealArrangementEn',
  'consumptionInfo', 'consumptionInfoEn', 'costInclusion', 'costInclusionEn',
  'playNotices', 'playNoticesEn', 'importantTips', 'importantTipsEn',
  'purchaseTerms', 'purchaseTermsEn',
  'attachmentIds', 'pickupPoints',
];

const isFilled = (v) => v != null && String(v).trim() !== '';

const MD_MAX_BYTES = 1024 * 1024;

// 读取上传的 Markdown 文件为文本（UTF-8，去掉 BOM、统一换行）
const readMdFile = (file) => new Promise((resolve, reject) => {
  if (!/\.(md|markdown)$/i.test(file.name || '')) {
    reject(new Error('仅支持 .md / .markdown 文件'));
    return;
  }
  if (file.size > MD_MAX_BYTES) {
    reject(new Error('文件不能超过 1MB'));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || '').replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n'));
  reader.onerror = () => reject(new Error('文件读取失败'));
  reader.readAsText(file, 'utf-8');
});

const StepNoticeTemplate = ({ tour, onRefresh }) => {
  const navigate = useNavigate();
  const isDaytour = tour?.tourType === 'daytour';
  const sections = isDaytour ? DAYTOUR_SECTIONS : STANDARD_SECTIONS;

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(tour?.destinationId || null);
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // 各段落的本地修改草稿：{ [field]: { cn, en } }，未保存前只存在前端
  const [pendingEdits, setPendingEdits] = useState({});
  const pendingCount = Object.keys(pendingEdits).length;

  // 段落编辑弹窗
  const [editingSection, setEditingSection] = useState(null); // { field, label }
  const [editCn, setEditCn] = useState('');
  const [editEn, setEditEn] = useState('');
  const [translating, setTranslating] = useState(false);
  const [editView, setEditView] = useState('edit'); // edit | preview
  // 保存整个模板
  const [savingAll, setSavingAll] = useState(false);
  const [saveAsVisible, setSaveAsVisible] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [savingAs, setSavingAs] = useState(false);

  // 段落的当前生效值（本地草稿优先）
  const effectiveCn = (field) => pendingEdits[field] ? pendingEdits[field].cn : destination?.[field];
  const effectiveEn = (field) => pendingEdits[field] ? pendingEdits[field].en : destination?.[`${field}En`];

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/destinations`, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) {
        const all = res.data.data || [];
        setTemplates(all.filter(d => isDaytour ? d.category === 'daytour' : d.category !== 'daytour'));
      }
    } catch (err) {
      console.error('获取模板列表失败:', err);
    }
  }, [isDaytour]);

  const fetchDestination = useCallback(async (id) => {
    if (!id) {
      setDestination(null);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/destinations/${id}`, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) setDestination(res.data.data);
    } catch (err) {
      message.error('获取模板内容失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setSelectedTemplateId(tour?.destinationId || null);
  }, [tour?.destinationId]);

  useEffect(() => {
    fetchDestination(selectedTemplateId);
    setPendingEdits({});
  }, [selectedTemplateId, fetchDestination]);

  // 切换模板：有未保存修改时先确认
  const handleSelectTemplate = (val) => {
    if (pendingCount > 0) {
      Modal.confirm({
        title: '有未保存的修改',
        content: `当前模板有 ${pendingCount} 个段落的修改尚未保存，切换模板将丢弃这些修改。`,
        okText: '丢弃并切换',
        cancelText: '留在当前',
        onOk: () => setSelectedTemplateId(val),
      });
    } else {
      setSelectedTemplateId(val);
    }
  };

  // 应用所选模板到本团
  const handleApplyTemplate = async () => {
    try {
      setApplying(true);
      await axios.put(
        `${API_BASE}/tours/${tour.id}`,
        { destinationId: selectedTemplateId },
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('已将所选模板应用到本团');
      onRefresh && await onRefresh();
    } catch (err) {
      message.error('应用失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setApplying(false);
    }
  };

  const openSectionEdit = (field, label) => {
    setEditingSection({ field, label });
    setEditCn(effectiveCn(field) || '');
    setEditEn(effectiveEn(field) || '');
    setEditView('edit');
  };

  // 上传 MD：内容填入对应语言，进入预览，点「确定」后与手工编辑一样暂存为草稿
  const applyMdFile = async (file, lang, section) => {
    try {
      const text = await readMdFile(file);
      if (!text.trim()) {
        message.error(`${file.name} 内容为空`);
        return;
      }
      if (section) {
        setEditingSection(section);
        setEditCn(lang === 'cn' ? text : (effectiveCn(section.field) || ''));
        setEditEn(lang === 'en' ? text : (effectiveEn(section.field) || ''));
      } else if (lang === 'cn') {
        setEditCn(text);
      } else {
        setEditEn(text);
      }
      setEditView('preview');
      message.success(`已读取 ${file.name}`);
    } catch (err) {
      message.error(err.message);
    }
  };

  const mdUploadProps = (lang, section) => ({
    accept: '.md,.markdown',
    showUploadList: false,
    beforeUpload: (file) => {
      applyMdFile(file, lang, section);
      return false;
    },
  });

  const closeSectionEdit = () => {
    setEditingSection(null);
    setEditCn('');
    setEditEn('');
    setEditView('edit');
  };

  // 段落弹窗确定：只暂存草稿，不落库
  const handleSectionOk = () => {
    const field = editingSection.field;
    const origCn = destination?.[field] || '';
    const origEn = destination?.[`${field}En`] || '';
    setPendingEdits(prev => {
      const next = { ...prev };
      if ((editCn || '') === origCn && (editEn || '') === origEn) {
        delete next[field]; // 改回原样 = 无修改
      } else {
        next[field] = { cn: editCn, en: editEn };
      }
      return next;
    });
    closeSectionEdit();
  };

  const handleTranslate = async () => {
    if (!editCn.trim()) {
      message.warning('请先填写中文内容');
      return;
    }
    try {
      setTranslating(true);
      const res = await axios.post(`${API_BASE}/translate`, { text: editCn, targetLang: 'en' }, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) {
        setEditEn(res.data.data.translation);
        message.success('翻译成功');
      }
    } catch (err) {
      message.error('翻译失败');
    } finally {
      setTranslating(false);
    }
  };

  // 把全部草稿保存到当前模板
  const handleSaveAllToCurrent = async () => {
    try {
      setSavingAll(true);
      const payload = {};
      Object.entries(pendingEdits).forEach(([field, v]) => {
        payload[field] = v.cn || null;
        payload[`${field}En`] = v.en || null;
      });
      await axios.put(`${API_BASE}/destinations/${selectedTemplateId}`, payload, { headers: AuthService.getAuthHeaders() });
      message.success(`已将 ${pendingCount} 处修改保存到模板「${destination?.name || ''}」`);
      setPendingEdits({});
      fetchDestination(selectedTemplateId);
    } catch (err) {
      message.error('保存失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingAll(false);
    }
  };

  // 整个模板（含全部草稿修改）另存为新模板，并应用到本团
  const handleSaveAs = async () => {
    if (!saveAsName.trim()) {
      message.error('请输入新模板名称');
      return;
    }
    try {
      setSavingAs(true);
      const payload = { name: saveAsName.trim(), category: destination?.category || (isDaytour ? 'daytour' : 'standard'), isActive: true };
      TEMPLATE_COPY_FIELDS.forEach(f => {
        if (destination?.[f] !== undefined) payload[f] = destination[f];
      });
      // 全部草稿修改覆盖进去
      Object.entries(pendingEdits).forEach(([field, v]) => {
        payload[field] = v.cn || null;
        payload[`${field}En`] = v.en || null;
      });
      const res = await axios.post(`${API_BASE}/destinations`, payload, { headers: AuthService.getAuthHeaders() });
      const newDest = res.data?.data;
      if (!newDest?.id) throw new Error('创建模板失败');
      await axios.put(
        `${API_BASE}/tours/${tour.id}`,
        { destinationId: newDest.id },
        { headers: AuthService.getAuthHeaders() }
      );
      message.success(`已另存为新模板「${saveAsName.trim()}」并应用到本团`);
      setSaveAsVisible(false);
      setSaveAsName('');
      setPendingEdits({});
      await fetchTemplates();
      setSelectedTemplateId(newDest.id);
      onRefresh && await onRefresh();
    } catch (err) {
      message.error('另存失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingAs(false);
    }
  };

  const openTemplateEditor = () => {
    // DestinationManagement 挂载时会读取 destEditingId 并自动打开编辑抽屉
    localStorage.setItem('destEditingId', selectedTemplateId);
    navigate('/admin/destinations');
  };

  const filledCount = sections.filter(([field]) => isFilled(effectiveCn(field))).length;
  const isCurrentApplied = selectedTemplateId === tour?.destinationId;

  const columns = [
    {
      title: '段落',
      dataIndex: 'label',
      key: 'label',
      width: 190,
      render: (label, r) => (
        <Space size={6}>
          {label}
          {pendingEdits[r.field] && <Tag color="orange" style={{ margin: 0 }}>已修改</Tag>}
        </Space>
      ),
    },
    {
      title: '中文',
      key: 'cn',
      width: 90,
      render: (_, r) => isFilled(effectiveCn(r.field))
        ? <Tag color="success">已填写</Tag>
        : <Tag>未填写</Tag>,
    },
    {
      title: 'English',
      key: 'en',
      width: 90,
      render: (_, r) => isFilled(effectiveEn(r.field))
        ? <Tag color="success">已填写</Tag>
        : <Tag>未填写</Tag>,
    },
    {
      title: '内容预览',
      key: 'preview',
      render: (_, r) => {
        const v = effectiveCn(r.field);
        if (!isFilled(v)) return <span style={{ color: '#bbb' }}>—</span>;
        return (
          <span style={{ color: '#666', fontSize: 12, display: 'inline-block', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v}>
            {v}
          </span>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, r) => {
        const section = { field: r.field, label: r.label };
        return (
          <Space size={8}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openSectionEdit(r.field, r.label)}>
              编辑
            </Button>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'cn', label: <Upload {...mdUploadProps('cn', section)}>中文内容</Upload> },
                  { key: 'en', label: <Upload {...mdUploadProps('en', section)}>English</Upload> },
                ],
              }}
            >
              <Button size="small" icon={<UploadOutlined />}>
                上传 MD <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  if (!tour) return null;

  return (
    <div>
      <Card title={<span><FileTextOutlined style={{ marginRight: 6 }} />选择通知书模板</span>} size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ width: 360 }}
            placeholder="选择通知书模板"
            value={selectedTemplateId}
            onChange={handleSelectTemplate}
            showSearch
            optionFilterProp="label"
            options={templates.map(t => ({
              value: t.id,
              label: t.id === tour.destinationId ? `${t.name}（本团当前）` : t.name,
            }))}
          />
          {!isCurrentApplied && selectedTemplateId && (
            <Button type="primary" icon={<CheckOutlined />} loading={applying} onClick={handleApplyTemplate}>
              应用到本团
            </Button>
          )}
          {isCurrentApplied && selectedTemplateId && <Tag color="success">本团当前模板</Tag>}
        </Space>
      </Card>

      {selectedTemplateId ? (
        <Spin spinning={loading}>
          {pendingCount > 0 ? (
            <Alert
              type="warning"
              showIcon
              message={`有 ${pendingCount} 个段落已修改，尚未保存`}
              description="可以继续编辑其他段落，全部改完后：「保存到当前模板」直接更新本模板，或「另存为新模板」把整个模板（含所有修改）以新名称保存并应用到本团。"
              style={{ marginBottom: 16 }}
              action={
                <Space direction="vertical">
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={() => {
                      const dateSuffix = tour?.departureDate ? dayjs(tour.departureDate).format('YYYY-MM-DD') : '定制';
                      setSaveAsName(`${destination?.name || '模板'}-${dateSuffix}`);
                      setSaveAsVisible(true);
                    }}
                    block
                  >
                    另存为新模板…
                  </Button>
                  <Button size="small" icon={<SaveOutlined />} loading={savingAll} onClick={handleSaveAllToCurrent} block>
                    保存到当前模板
                  </Button>
                  <Button size="small" icon={<UndoOutlined />} onClick={() => setPendingEdits({})} block>
                    放弃修改
                  </Button>
                </Space>
              }
            />
          ) : (
            <Alert
              type={filledCount === sections.length ? 'success' : 'info'}
              showIcon
              message={`模板段落已填写 ${filledCount}/${sections.length}`}
              description="点击段落的「编辑」修改内容，可连续修改多个段落，最后统一保存或另存为新模板。"
              style={{ marginBottom: 16 }}
            />
          )}
          <Card
            title={<span>出团模板 — {destination?.name || '-'}{pendingCount > 0 && <Tag color="orange" style={{ marginLeft: 8 }}>{pendingCount} 处未保存</Tag>}</span>}
            size="small"
            extra={
              <Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => { setPendingEdits({}); fetchDestination(selectedTemplateId); }}>刷新</Button>
                <Tooltip title="打开完整模板编辑器（含行程、附件等）">
                  <Button size="small" icon={<EditOutlined />} onClick={openTemplateEditor}>完整编辑</Button>
                </Tooltip>
              </Space>
            }
          >
            <Table
              rowKey="field"
              size="small"
              columns={columns}
              dataSource={sections.map(([field, label]) => ({ field, label }))}
              pagination={false}
            />
          </Card>

          {tour.tourType === 'standard' && isCurrentApplied && (
            <ItineraryEnCard destinationId={tour.destinationId} />
          )}
        </Spin>
      ) : (
        <Alert
          type="warning"
          showIcon
          message="本团尚未关联通知书模板"
          description="请在上方选择一个模板并应用到本团。"
        />
      )}

      {/* 段落编辑弹窗：确定只暂存修改，统一在页面上保存 */}
      <Modal
        title={`编辑段落 — ${editingSection?.label || ''}`}
        open={!!editingSection}
        onCancel={closeSectionEdit}
        onOk={handleSectionOk}
        width={860}
        okText="确定"
        cancelText="取消"
      >
        <Segmented
          value={editView}
          onChange={setEditView}
          options={[{ label: '编辑', value: 'edit' }, { label: '预览', value: 'preview' }]}
          style={{ marginBottom: 12 }}
        />
        <div style={{ marginBottom: 12 }}>
          <Space size={8} style={{ marginBottom: 4 }}>
            <span style={{ fontWeight: 500 }}>中文内容</span>
            <Upload {...mdUploadProps('cn')}>
              <Button size="small" icon={<UploadOutlined />}>上传 MD</Button>
            </Upload>
          </Space>
          {editView === 'edit' ? (
            <TextArea rows={8} value={editCn} onChange={(e) => setEditCn(e.target.value)} placeholder="请输入中文内容，支持 Markdown 格式" />
          ) : (
            <div className="md-preview-box">
              {isFilled(editCn) ? <MarkdownContent text={editCn} /> : <span style={{ color: '#bbb' }}>未填写</span>}
            </div>
          )}
        </div>
        <div>
          <Space size={8} style={{ marginBottom: 4 }}>
            <span style={{ fontWeight: 500 }}>English</span>
            <Upload {...mdUploadProps('en')}>
              <Button size="small" icon={<UploadOutlined />}>上传 MD</Button>
            </Upload>
            <Tooltip title="翻译到英文">
              <Button type="primary" size="small" icon={<TranslationOutlined />} loading={translating} onClick={handleTranslate} />
            </Tooltip>
          </Space>
          {editView === 'edit' ? (
            <TextArea rows={8} value={editEn} onChange={(e) => setEditEn(e.target.value)} placeholder="English content, Markdown supported" />
          ) : (
            <div className="md-preview-box">
              {isFilled(editEn) ? <MarkdownContent text={editEn} /> : <span style={{ color: '#bbb' }}>未填写</span>}
            </div>
          )}
        </div>
        <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
          点「确定」暂存本段修改；改完所有段落后，在页面上统一「保存到当前模板」或「另存为新模板」。
        </div>
      </Modal>

      {/* 另存为命名弹窗：整个模板（含全部修改）另存 */}
      <Modal
        title="另存为新模板"
        open={saveAsVisible}
        onCancel={() => setSaveAsVisible(false)}
        onOk={handleSaveAs}
        confirmLoading={savingAs}
        okText="创建并应用"
      >
        <div style={{ marginBottom: 8 }}>新模板名称：</div>
        <Input
          value={saveAsName}
          onChange={(e) => setSaveAsName(e.target.value)}
          placeholder={`如：${destination?.name || '模板'}-定制`}
          onPressEnter={handleSaveAs}
        />
        <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
          将复制模板「{destination?.name}」的全部内容（含本次 {pendingCount} 处修改），创建新模板并应用到本团。原模板不受影响。
        </div>
      </Modal>
    </div>
  );
};

export default StepNoticeTemplate;
